import { WifiOff } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { Outlet, useParams } from 'react-router';
import { trapFocus } from '../../components/Modal';
import { MOBILE_QUERY, useMediaQuery } from '../../lib/useMediaQuery';
import { useConnection } from '../../realtime/gateway';
import { useAppStore } from '../../store/app';
import { useUi } from '../../store/ui';
import { setSoundboardVoiceState, stopSoundboardSounds } from '../extras';
import { VoiceStatusBar } from '../voice';
import { useVoiceStore } from '../voice/store';
import { DmSidebar } from './DmSidebar';
import { GlobalOverlays } from './GlobalOverlays';
import { ServerRail } from './ServerRail';
import { ServerSidebar } from './ServerSidebar';
import { Splash } from './Splash';
import { UserPanel } from './UserPanel';
import './shell.css';

/** Wartet auf READY (bzw. den REST-Ersatz), bevor die App gerendert wird. */
export function ReadyGate({ children }: { children: React.ReactNode }) {
  const ready = useAppStore((s) => s.ready);
  if (!ready) return <Splash />;
  return <>{children}</>;
}

/** Layout: Serverleiste | Kanalliste | Inhalt (Chat + Mitglieder/Thread-Panel). Mobil als Drawer. */
export function AppShell() {
  const { serverId } = useParams();
  const navOpen = useUi((s) => s.navOpen);
  const isServer = !!serverId && serverId !== '@me';
  const mobile = useMediaQuery(MOBILE_QUERY);
  const drawerRef = useNavDrawer(mobile && navOpen);
  /** Mobil geschlossen: aus Tab-Reihenfolge und A11y-Baum nehmen. */
  const drawerHidden = mobile && !navOpen;
  useSoundboardVoiceBridge();
  return (
    <div className={`app-shell ${navOpen ? 'nav-open' : ''}`} data-testid="app-shell">
      <a className="skip-link" href="#lf-main">
        Zum Inhalt springen
      </a>
      <div
        className="nav-drawer"
        ref={drawerRef}
        inert={drawerHidden}
        aria-hidden={drawerHidden || undefined}
        role={mobile && navOpen ? 'dialog' : undefined}
        aria-modal={mobile && navOpen ? true : undefined}
        aria-label={mobile ? 'Navigation' : undefined}
        data-testid="nav-drawer"
        data-open={mobile ? String(navOpen) : undefined}
      >
        <ServerRail />
        <aside className="sidebar" aria-label={isServer ? 'Kanäle' : 'Direktnachrichten'}>
          {isServer ? <ServerSidebar serverId={serverId} /> : <DmSidebar />}
          <VoiceStatusBar />
          <UserPanel />
        </aside>
      </div>
      {navOpen && <div className="drawer-scrim" onClick={() => useUi.getState().setNavOpen(false)} aria-hidden="true" data-testid="nav-scrim" />}
      <main className="main" id="lf-main" tabIndex={-1}>
        <ConnectionBanner />
        <Outlet />
      </main>
      <GlobalOverlays />
    </div>
  );
}

/**
 * Soundboard (Extras) ↔ Voice: SOUNDBOARD_PLAY nur abspielen, wenn dieser Client mit genau dem Kanal verbunden und
 * nicht taub ist; beim Verlassen/Wechseln des Kanals laufende Sounds stoppen.
 */
function useSoundboardVoiceBridge() {
  useEffect(() => {
    const unbind = setSoundboardVoiceState(() => {
      const v = useVoiceStore.getState();
      return { channelId: v.status === 'idle' ? null : v.channelId, deafened: v.selfDeaf || v.serverDeaf };
    });
    const unsub = useVoiceStore.subscribe((s, prev) => {
      if (s.channelId !== prev.channelId) stopSoundboardSounds();
    });
    return () => {
      unsub();
      unbind();
    };
  }, []);
}

/**
 * Mobiler Navigations-Drawer: beim Öffnen Fokus hinein, Escape schließt, beim Schließen Fokus zurück
 * auf das Element, das ihn geöffnet hat.
 */
function useNavDrawer(open: boolean) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const prev = document.activeElement as HTMLElement | null;
    const el = ref.current;
    const target = el?.querySelector<HTMLElement>('[aria-current="page"], [aria-selected="true"], a[href], button:not([disabled])');
    target?.focus();
    const onKey = (e: KeyboardEvent) => {
      // Dialoge/Popover darüber fangen Escape in der Capture-Phase ab (stopPropagation).
      if (e.key === 'Escape' && !e.defaultPrevented) useUi.getState().setNavOpen(false);
      if (e.key === 'Tab' && el) trapFocus(el, e);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      if (prev && document.contains(prev) && el?.contains(document.activeElement)) prev.focus();
    };
  }, [open]);
  return ref;
}

function ConnectionBanner() {
  const state = useConnection((s) => s.state);
  if (state === 'connected' || state === 'idle' || state === 'connecting') return null;
  return (
    <div className="connection-banner" role="status" data-testid="connection-banner" data-state={state}>
      <WifiOff size={16} />
      {state === 'offline' ? 'Keine Internetverbindung – Nachrichten werden nach der Rückkehr nachgeladen.' : 'Verbindung wird wiederhergestellt …'}
    </div>
  );
}
